import { Operator } from '@/sections/insights/queryBuilder/legacy/types/queryBuilder.types';
import { AssetStatus } from '@/types';

/**
 * Centralized asset status filter mapping
 * This is the single source of truth for asset status filtering logic
 * Used by both useAssets hook and QueryBuilder
 */
export interface AssetStatusFilter {
  field: string;
  operator: string | Operator;
  value: string | AssetStatusFilter[];
}

export const getAssetStatusFilterMap = (): Record<
  string,
  AssetStatusFilter
> => {
  return {
    // Approved: any active asset, regardless of when it was last seen
    A: {
      field: 'status',
      operator: '=',
      value: 'A',
    },

    [AssetStatus.Unavailable]: {
      field: 'status',
      operator: '=',
      value: AssetStatus.Unavailable,
    },

    // Simple status filters
    [AssetStatus.Pending]: {
      field: 'status',
      operator: '=',
      value: AssetStatus.Pending,
    },
    [AssetStatus.Frozen]: {
      field: 'status',
      operator: 'STARTS WITH',
      value: AssetStatus.Frozen,
    },
    [AssetStatus.Deleted]: {
      field: 'status',
      operator: '=',
      value: AssetStatus.Deleted,
    },
  };
};

/**
 * Helper function to build asset status filters for queries
 * Handles both single and multiple status values
 */
export const buildAssetStatusFilter = (
  statusValues: string[]
): AssetStatusFilter | null => {
  if (!statusValues || statusValues.length === 0) {
    return null;
  }

  const filterMap = getAssetStatusFilterMap();

  if (statusValues.length === 1) {
    // Single status value
    const statusFilter = filterMap[statusValues[0]];
    return statusFilter || null;
  }

  // Multiple status values - use OR logic
  const statusFilters = statusValues
    .map(status => filterMap[status])
    .filter(Boolean);

  if (statusFilters.length === 0) {
    return null;
  }

  return {
    field: '',
    operator: 'OR',
    value: statusFilters,
  };
};
